import { ReactElement, useMemo } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { useWindowSize } from "usehooks-ts";
import { ReactQueryKeys } from "../../../Constants/ClientRoutingConstants";
import areasApiService from "../../Area/Utilities/AreasApiService";
import { useQuery, useQueryClient } from "react-query";
import assetsApiService from "../Utilities/AssetsApiService";
import { useToast } from "@chakra-ui/react";
import { useLocalization } from "@progress/kendo-react-intl";
import { enMessages } from "../../../messages/en-US";
import LoadingSpinner from "../../Shared/Components/LoadingSpinner";
import ErrorCard from "../../Shared/Components/Cards/ErrorCard";
import { MachineReadDto } from "../../../Models/Machine/MachinesReadDto";
import AreaReadDto from "../../../Models/Area/AreaReadDto";
import { MachineReadTableDto } from "../../../Models/Machine/MachineReadTableDto";
import AssetsTable from "../Components/Tables/AssetsTable";
import { usesAssetApi } from "../Utilities/AssetDetailsUtils";

interface Props {
  isOpenDialog: any;
  setIsOpenDialog: any;
}

export default function AssetsPage(props: Props): ReactElement {

   //Server side state
   const client = useQueryClient();
   const toast = useToast({
    position: "bottom",
    duration: 5000,
    isClosable: true,
  });
   
  // Get window size for dialog height
  const { height } = useWindowSize();
  const localizationService = useLocalization();

  const assetQuery = useQuery<Array<MachineReadDto>>(
    ReactQueryKeys.AllMachinesQuery, 
    async () => assetsApiService.getAllMachines()
  );

  const areasQuery = useQuery<Array<AreaReadDto>>(ReactQueryKeys.AllAreasQuery, async () => {
    return areasApiService.getAllAreas();
  }); 

  const createAsset = async (values: any) => {
    try {
      if(usesAssetApi(values.machineryType)) {
        await assetsApiService.createAsset(values);
      } else {
        await assetsApiService.createDefaultAsset(values);
      }
      
      // Use the same query key as the initial fetch
      client.refetchQueries(ReactQueryKeys.AllMachinesQuery);
      // Refetch endpoints as well
      client.refetchQueries(ReactQueryKeys.AllEndpointsQuery);
  
      toast({
        status: "success",
        description: `'${values.name}' ${localizationService.toLanguageString('custom.assetCreated', enMessages.custom.assetCreated)}`,
      });
  
      return true;
    } catch (e: any) {
      toast({
        title: localizationService.toLanguageString('custom.error', enMessages.custom.error),
        status: "error",
        description: localizationService.toLanguageString('custom.assetsCreatedError', enMessages.custom.assetsCreatedError),
      });
  
      return false;
    }
  };

  const updateAsset = async (asset: any) => {
    try {
      if(usesAssetApi(asset.machineryType)) {
        await assetsApiService.updateAsset(asset);
      } else {
        await assetsApiService.updateDefaultAsset(asset);
      }
      //If update is successful, refetch the updated list of areas and show success message
      client.refetchQueries(ReactQueryKeys.AllMachinesQuery);
      // Refetch endpoints as well
      client.refetchQueries(ReactQueryKeys.AllEndpointsQuery);
      toast({
        status: "success",
        description: `${localizationService.toLanguageString('custom.assetsUpdated', enMessages.custom.assetsUpdated)}`,
      });
    } catch (e: any) {
      var errorMessage;
      errorMessage = `${localizationService.toLanguageString('custom.assetsUpdatedError', enMessages.custom.assetsUpdatedError)}.\n
      ${e?.response?.data?.errors ? Object.values(e?.response?.data?.errors)[0] : ""}`;
      errorMessage = `${localizationService.toLanguageString('custom.assetsIncorrectInput', enMessages.custom.assetsIncorrectInput)}\n
        ${e?.response?.data?.map((assetId: string) => asset.find((a) => a.id === assetId)?.name)}
      `;
      toast({
        status: "error",
        description: errorMessage,
      });
    }
  };

  const deleteAsset = async (selectedAsset: any) => {
    try {
      if(usesAssetApi(selectedAsset.machineryType)) {
        await assetsApiService.deleteAsset(selectedAsset.id);
      } else {
        await assetsApiService.deleteDefaultAsset(selectedAsset.id);
      }
      //If delete is successful, refetch the updated list of areas and show success message
      client.refetchQueries(ReactQueryKeys.AllMachinesQuery);
      // Refetch endpoints as well
      client.refetchQueries(ReactQueryKeys.AllEndpointsQuery);
      toast({
        status: "success",
        description: `${localizationService.toLanguageString('custom.assetsDeleted', enMessages.custom.assetsDeleted)}`,
      });
    } catch (e) {
      toast({
        status: "error",
        description: `${localizationService.toLanguageString('custom.assetsDeletedError', enMessages.custom.assetsDeletedError)}`,
      });
    }
  };
  

  const assetData = useMemo(() => {
    //Create hashmap of areaId -> name pairs.
    const areaIdtoName: { [id: string]: string } = {};
    const machineIdtoName: { [id: string]: string } = {};
    const pointTag: { [id: string]: number } = {};
    areasQuery.data?.forEach((area) => {
      areaIdtoName[area.id] = area.name;
    });
    assetQuery.data?.forEach((machine) => {
      machineIdtoName[machine.id] = machine.name;
    });

    //Map over machines to inject relevant data
    const results =  assetQuery.data?.map((machine) => {
      return {
        ...machine,
        parentName: machineIdtoName[machine.parentId] ?? null,
        areaName: areaIdtoName[machine.areaId] ?? null,
        pointTag: pointTag[machine.id] ?? 0,

      } as MachineReadTableDto;
    });
    
    return results;
  }, [areasQuery.data, assetQuery.data]);


  if(assetQuery.isLoading || assetQuery.isFetching) {
    return <LoadingSpinner />;
  }

  if(assetQuery.error) {
    return <ErrorCard />;
  }
  return (
    <div>
      {/* Main Table */}
      <div className="mt-4">
        <AssetsTable
          assets={assetData}
          height={690}
          createAsset={createAsset}
          updateAsset={updateAsset}
          deleteAsset={deleteAsset}
        />
      </div>

      {/* Dialog version of the table */}
      {props.isOpenDialog.assets && (
        <Dialog
          title="Assets"
          onClose={() => props.setIsOpenDialog(false)}
        >
          <AssetsTable
            assets={assetData}
            height={height - 200}
            createAsset={createAsset}
            updateAsset={updateAsset}
            deleteAsset={deleteAsset}
          />
        </Dialog>
      )}
    </div>
  );
}
